"use strict";
// packages/shared-react/src/hooks/useConversations.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiConversationList = useAgentApiConversationList;
exports.useAgentApiConversation = useAgentApiConversation;
const recoil_1 = require("recoil");
const react_1 = require("react");
const shared_1 = require("@heartcheck/shared");
const state_1 = require("../state");
function tsNum(t) {
    if (!t)
        return 0;
    const n = Date.parse(t);
    return Number.isFinite(n) ? n : 0;
}
/**
 * IMPORTANT:
 * listConversations is owner-protected; it must be called with a Cognito *ID token*.
 * Do not fall back to IAM for this field.
 */
const listConversationsGql = /* GraphQL */ `
  query ListConversations {
    listConversations {
      id
      timestamp
      agent
      origin
    }
  }
`;
const loadConversationQueryUserPools = new shared_1.GraphqlQuery(listConversationsGql, {
    forwardAuthMode: "userpools",
});
/**
 * Normalize any raw value from the Conversations atom into a proper
 * Loadable<ObjRecord<Conversation>>. This protects us from legacy / bad writes.
 */
function normalizeConversationsState(raw) {
    if (!raw) {
        return state_1.Loadable.unloaded();
    }
    const candidate = raw;
    if (typeof candidate.isUnloaded === "function") {
        return candidate;
    }
    // Fallback if something non-Loadable was stored.
    return state_1.Loadable.unloaded();
}
/**
 * Backwards-compatible hook:
 *
 * - New code:
 *     const conversationsLoadable = useAgentApiConversationList();
 *     if (conversationsLoadable.isUnloaded()) ...
 *
 * - Old code:
 *     const [conversationsLoadable, refreshConversations] =
 *       useAgentApiConversationList();
 *
 * Returned value is an array with attached helpers:
 *   result[0] === conversationsState
 *   result.refresh()
 *   result.value
 *   result.isUnloaded()
 */
function useAgentApiConversationList() {
    const [rawConversationsState, setConversationsState] = (0, recoil_1.useRecoilState)(state_1.Conversations);
    const conversationsState = normalizeConversationsState(rawConversationsState);
    const [, setConversationEventsState] = (0, recoil_1.useRecoilState)(state_1.ConversationEvents);
    const [, setConversationMetadata] = (0, recoil_1.useRecoilState)(state_1.ConversationPartialResults);
    const refresh = (0, react_1.useCallback)(() => {
        // Force full reload on next effect run
        setConversationsState(state_1.Loadable.unloaded());
        setConversationEventsState({});
        setConversationMetadata({});
    }, [setConversationsState, setConversationEventsState, setConversationMetadata]);
    (0, react_1.useEffect)(() => {
        if (!conversationsState.isUnloaded())
            return;
        setConversationsState(state_1.Loadable.loading());
        const run = async () => {
            // Ensure we have an ID token (owner rules require Cognito identity)
            const idToken = await (0, shared_1.getIdTokenWithFallback)();
            if (!idToken) {
                throw new Error("Missing Cognito ID token. Sign in again, or ensure the app stores the ID token for AppSync USER_POOLS auth.");
            }
            // USER_POOLS only — no IAM fallback here
            let result;
            try {
                const value = await loadConversationQueryUserPools.invoke();
                result = value;
            }
            catch (err) {
                const msg = String(err?.message ?? "");
                if (/not authorized|unauthorized/i.test(msg)) {
                    throw new Error("User Pools authorization failed for listConversations. " +
                        "Double-check that: (1) the schema allows @aws_cognito_user_pools on Query.listConversations, " +
                        "(2) you’re sending the *ID token* (not the access token), and (3) your resolver uses identity.sub as the owner.");
                }
                throw err;
            }
            const raw = Array.isArray(result?.listConversations)
                ? result.listConversations
                : [];
            // Only include manual-origin chats and sort newest first
            const filtered = raw
                .filter((c) => c?.origin === "manual")
                .sort((a, b) => tsNum(b.timestamp) - tsNum(a.timestamp));
            // Normalize to full Conversation objects with events: []
            const normalized = filtered.map((c) => ({
                id: c.id,
                timestamp: c.timestamp,
                agent: c.agent,
                origin: c.origin ?? "manual",
                events: [],
            }));
            const conversationMap = state_1.ObjRecord.of(normalized);
            // Seed empty events for each conversation
            const eventMap = normalized.reduce((acc, convo) => {
                acc[convo.id] = state_1.Loadable.loaded([]);
                return acc;
            }, {});
            // Seed conversation-scoped metadata (active agent & empty partials)
            setConversationMetadata((prev) => {
                const next = { ...prev };
                for (const convo of normalized) {
                    const agentId = typeof convo.agent === "string" && convo.agent.length > 0
                        ? convo.agent
                        : undefined;
                    if (!agentId)
                        continue;
                    const existingConv = next[convo.id];
                    const existingByAgent = existingConv?.byAgent ?? {};
                    const initialAgentMeta = {
                        agentStartResponding: null,
                        agentStopResponding: null,
                        agentPartialMessage: "",
                        updatedAt: new Date().toISOString(),
                    };
                    next[convo.id] = {
                        activeAgentId: existingConv?.activeAgentId ?? agentId,
                        byAgent: {
                            ...existingByAgent,
                            [agentId]: {
                                ...(existingByAgent[agentId] ?? {}),
                                ...initialAgentMeta,
                            },
                        },
                    };
                }
                return next;
            });
            setConversationsState(state_1.Loadable.loaded(conversationMap));
            setConversationEventsState(eventMap);
        };
        run().catch((err) => {
            // eslint-disable-next-line no-console
            console.error("❌ Failed to load conversations:", err);
            // IMPORTANT: don't use Loadable.error() here, or we’ll look "unloaded"
            // and re-run this effect in a tight loop. Instead, mark as loaded with
            // an empty record.
            setConversationsState(state_1.Loadable.loaded(state_1.ObjRecord.empty()));
        });
    }, [
        conversationsState,
        setConversationsState,
        setConversationEventsState,
        setConversationMetadata,
    ]);
    // ----- Backwards-compatible return shape -----
    const current = normalizeConversationsState(rawConversationsState);
    const result = [current, refresh];
    result.state = current;
    result.value = current.value;
    result.loading = current.loading === "loading";
    // Our Loadable doesn't formally track error; keep the property for legacy callers.
    result.error = current.error ?? undefined;
    result.isUnloaded = current.isUnloaded.bind(current);
    result.refresh = refresh;
    return result;
}
function useAgentApiConversation(id = "") {
    const conversations = useAgentApiConversationList();
    // Try to pull out the underlying Loadable in a tolerant way:
    const stateCandidate = conversations && conversations.state
        ? conversations.state
        : Array.isArray(conversations)
            ? conversations[0]
            : conversations;
    const state = stateCandidate && typeof stateCandidate.isUnloaded === "function"
        ? stateCandidate
        : state_1.Loadable.unloaded();
    if (state.isUnloaded() || state.loading === "loading" || !id) {
        return state_1.Loadable.loading();
    }
    const value = state.value;
    let item;
    if (value && typeof value.get === "function") {
        // ObjRecord<Conversation> case
        item = value.get(id);
    }
    else if (value && typeof value.items === "function") {
        // Fallback: items() → Conversation[]
        const arr = value.items();
        item = arr.find((c) => c.id === id);
    }
    else if (Array.isArray(value)) {
        // Fallback: plain array
        item = value.find((c) => c.id === id);
    }
    return item ? state_1.Loadable.loaded(item) : state_1.Loadable.loading();
}
